import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getAllPosts, Post } from '../utils/blogLoader';
import ScrollToTop from '../components/ScrollToTop';
import {
  PostCard,
  PostImage,
  PostContent,
  PostTitle,
  PostExcerpt,
  PostMeta,
  PostTags,
  Tag
} from '../styles/components';

const BlogContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const BlogList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 2rem;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.textSecondary};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem;
  background: ${({ theme }) => theme.surfaceAlt};
  border-radius: 8px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme, $active }) => ($active ? theme.primary : theme.surface)};
  color: ${({ theme, $active }) => ($active ? theme.surface : theme.text)};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    filter: brightness(0.96);
  }

  &:disabled {
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme }) => theme.textSecondary};
    cursor: not-allowed;
    filter: none;
  }
`;

const PageInfo = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const Blog: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 5;
  const navigate = useNavigate();
  const location = useLocation();

  // 保存滚动位置的函数
  const saveScrollPosition = () => {
    const scrollPosition = window.scrollY;
    console.log('Saving scroll position:', scrollPosition);
    sessionStorage.setItem('blogScrollPosition', scrollPosition.toString());
  };

  // 恢复滚动位置的函数
  const restoreScrollPosition = () => {
    const scrollPosition = sessionStorage.getItem('blogScrollPosition');
    console.log('Restoring scroll position:', scrollPosition);
    if (scrollPosition) {
      const position = parseInt(scrollPosition, 10);
      window.scrollTo({
        top: position,
        behavior: 'instant' // 使用 instant 而不是 smooth，避免动画延迟
      });
      sessionStorage.removeItem('blogScrollPosition');
    }
  };

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const allPosts = await getAllPosts();
        console.log('Loaded posts:', allPosts);
        setPosts(allPosts);
      } catch (error) {
        console.error('Error loading posts:', error);
        setError('Failed to load posts');
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  // 从 URL 中恢复分页状态
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = searchParams.get('page');
    if (page) {
      const pageNumber = parseInt(page, 10);
      if (!isNaN(pageNumber) && pageNumber > 0) {
        setCurrentPage(pageNumber);
      }
    }
  }, [location.search]);

  // 恢复滚动位置
  useEffect(() => {
    if (!loading) {
      // 等待 DOM 更新完成后再恢复滚动位置
      setTimeout(restoreScrollPosition, 100);
    }
  }, [loading, currentPage]);

  const handlePostClick = (slug: string) => {
    saveScrollPosition();
    navigate(`/blog/${slug}?page=${currentPage}`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    navigate(`/blog?page=${page}`, { replace: true });
  };

  const totalPages = Math.ceil(posts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const endIndex = startIndex + postsPerPage;
  const currentPosts = posts.slice(startIndex, endIndex);

  if (loading) {
    return <LoadingContainer>Loading...</LoadingContainer>;
  }

  if (error) {
    return <LoadingContainer>{error}</LoadingContainer>;
  }

  return (
    <BlogContainer>
      {currentPosts.length === 0 ? (
        <LoadingContainer>No posts found</LoadingContainer>
      ) : (
        <>
          <BlogList>
            {currentPosts.map((post) => (
              <PostCard 
                key={post.slug}
                onClick={() => handlePostClick(post.slug)}
              >
                <PostImage>
                  {post.image ? (
                    <img src={post.image} alt={post.title} loading="lazy" />
                  ) : post.video?.thumbnail ? (
                    <img src={post.video.thumbnail} alt={post.title} loading="lazy" />
                  ) : (
                    post.title.charAt(0)
                  )}
                </PostImage>
                <PostContent>
                  <PostTitle>{post.title}</PostTitle>
                  <PostMeta>
                    By {post.author} • {new Date(post.date).toLocaleDateString()}
                  </PostMeta>
                  <PostExcerpt>{post.excerpt}</PostExcerpt>
                  {post.tags.length > 0 && (
                    <PostTags>
                      {post.tags.map((tag) => (
                        <Tag key={tag}>{tag}</Tag>
                      ))}
                    </PostTags>
                  )}
                </PostContent>
              </PostCard>
            ))}
          </BlogList>
          {totalPages > 1 && (
            <>
              <Pagination>
                <PageButton
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </PageButton>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <PageButton
                    key={page}
                    $active={currentPage === page}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </PageButton>
                ))}
                <PageButton
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </PageButton>
              </Pagination>
              <PageInfo>
                Page {currentPage} of {totalPages}
              </PageInfo>
            </>
          )}
        </>
      )}
      <ScrollToTop />
    </BlogContainer>
  );
};

export default Blog; 